/**
 * Handlers for contextual response actions (navigate + create).
 * Frontend never decides actions — only executes server payloads.
 */

import { LENS_CONTEXTUAL_ACTIONS_TELEMETRY_ENABLED, LEGACY_CREATE_KEY_HANDLERS } from './responseActions';

const CREATE_KEY_HANDLERS = LEGACY_CREATE_KEY_HANDLERS;

export function prefillToSidebarContent(prefill, fallbackText = '') {
    if (!prefill || typeof prefill !== 'object') return fallbackText;
    return (
        prefill.description ||
        prefill.request_text ||
        prefill.context ||
        prefill.title ||
        fallbackText
    );
}

export function buildNavigatePath(action, projectId) {
    if (!action?.route) return null;
    const base = String(action.route);
    const target = action.target && typeof action.target === 'object' ? action.target : null;
    const targetId = target?.id;
    const targetType = target?.type;

    if (targetId && targetType === 'irl') {
        return `${base}?irlId=${encodeURIComponent(targetId)}`;
    }
    if (targetId && targetType === 'raid') {
        return `${base}?raidId=${encodeURIComponent(targetId)}`;
    }
    if (targetId && targetType === 'action_log') {
        return `${base}?entryId=${encodeURIComponent(targetId)}`;
    }
    if (targetId && targetType === 'task') {
        return `${base}?taskId=${encodeURIComponent(targetId)}`;
    }
    if (targetId && targetType === 'observation') {
        return `${base}?observationId=${encodeURIComponent(targetId)}`;
    }
    if (targetId && targetType === 'file') {
        return `${base}?fileId=${encodeURIComponent(targetId)}`;
    }
    if (targetId && targetType === 'folder') {
        return `${base}?folderId=${encodeURIComponent(targetId)}`;
    }
    if (targetId && targetType === 'workstream') {
        return `${base}?workstreamId=${encodeURIComponent(targetId)}`;
    }
    if (projectId && base.startsWith('/')) {
        return base;
    }
    return base;
}

export function handleNavigateAction({ navigate, action, projectId }) {
    const path = buildNavigatePath(action, projectId);
    if (!path || !navigate) return false;
    navigate(path);
    return true;
}

export function resolveCreateKind(actionKey) {
    return CREATE_KEY_HANDLERS[actionKey] || null;
}

export function recordActionClicked({ traceId, action, slot, projectId }) {
    if (!LENS_CONTEXTUAL_ACTIONS_TELEMETRY_ENABLED || !action) return;
    // Extension point for future analytics pipeline.
    if (typeof window !== 'undefined' && window.console?.debug) {
        window.console.debug('contextual_action_clicked', {
            trace_id: traceId,
            candidate_id: action.candidate_id,
            key: action.key,
            slot,
            project_id: projectId,
        });
    }
}

export function handleResponseAction({
    action,
    slot,
    traceId,
    projectId,
    navigate,
    handlers,
    answerText = '',
}) {
    if (!action) return;
    recordActionClicked({ traceId, action, slot, projectId });

    if (action.kind === 'navigate') {
        if (action.key === 'view_evidence') {
            handlers.onViewEvidence?.();
            return;
        }
        if (action.key === 'open_source') {
            handlers.onOpenSource?.(action);
            return;
        }
        if (action.key === 'save_to_journal') {
            handlers.onSaveJournal?.(answerText);
            return;
        }
        handleNavigateAction({ navigate, action, projectId });
        return;
    }

    const createKind = resolveCreateKind(action.key);
    const content = prefillToSidebarContent(action.prefill, answerText);
    if (createKind === 'observation') handlers.onCreateObservation?.(content);
    else if (createKind === 'task') handlers.onCreateTask?.(content);
    else if (createKind === 'irl') handlers.onCreateIrl?.(content);
    else if (createKind === 'raid') handlers.onCreateRaid?.(content);
    else if (createKind === 'action_log') handlers.onCreateActionLog?.(content);
}
